import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Shield, Clock, MessageSquare } from 'lucide-react';
import Layout from '../../components/layout/layout';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';
import BookingForm from '../../components/booking/BookingForm';

const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const reassurances = [
  {
    icon: Clock,
    title: 'Créneau indicatif',
    body: 'Vous indiquez vos préférences. Nous confirmons la disponibilité par email sous 24h.',
  },
  {
    icon: MessageSquare,
    title: 'Échange sans engagement',
    body: 'Cet appel ne vous engage à rien. C\'est l\'occasion de clarifier votre besoin et de voir si on peut vous aider.',
  },
  {
    icon: Shield,
    title: 'Données protégées',
    body: 'Vos informations sont utilisées uniquement pour organiser cet échange, conformément au RGPD.',
  },
  {
    icon: Calendar,
    title: 'Durée : 30 minutes',
    body: 'Un échange structuré, focalisé sur votre contexte, vos enjeux et les premières pistes concrètes.',
  },
];

export default function ReservationPage() {
  return (
    <Layout
      title="Réserver un échange - Recruteurs.io"
      description="Prenez rendez-vous avec un expert Recruteurs.io. 30 minutes pour parler de votre besoin en recrutement, sans engagement."
      breadcrumbs={[
        { name: 'Accueil', href: '/' },
        { name: 'Réserver un échange', href: '/reservation' },
      ]}
    >
      <WebPageSchema
        page={{
          title: 'Réserver un échange - Recruteurs.io',
          description:
            'Prenez rendez-vous avec un expert Recruteurs.io. 30 minutes pour parler de votre besoin en recrutement, sans engagement.',
          url: 'https://recruteurs.io/reservation',
          lastReviewed: '2026-04-27',
        }}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Réserver un échange', url: 'https://recruteurs.io/reservation' },
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white pb-0 pt-16 md:pt-20">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 h-96 w-96 rounded-full bg-[#d03484]/8 blur-3xl" />
          <div className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-[#d47254]/8 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-5xl px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 text-xs font-semibold uppercase tracking-widest text-neutral-500 shadow-sm ring-1 ring-neutral-200 mb-6">
              <Calendar className="h-3.5 w-3.5 text-[#d03484]" />
              Prise de rendez-vous
            </span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-neutral-950 leading-[1.06] mb-6">
              Réservez un{' '}
              <span className={gradientText}>échange de 30 min</span>
              {' '}avec notre équipe
            </h1>

            <p className="mx-auto max-w-2xl text-lg md:text-xl text-neutral-600 leading-relaxed mb-12">
              Parlez-nous de votre contexte. On vous aide à clarifier votre besoin
              et à identifier les premières pistes — sans jargon, sans engagement.
            </p>
          </motion.div>

          {/* Reassurance strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16"
          >
            {reassurances.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="rounded-2xl bg-[#fbfaf8] p-5 ring-1 ring-neutral-200 text-left"
                >
                  <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-r from-[#d03484]/15 to-[#d47254]/15">
                    <Icon className="h-4 w-4 text-[#d03484]" />
                  </div>
                  <p className="text-sm font-semibold text-neutral-900 mb-1">{item.title}</p>
                  <p className="text-xs text-neutral-500 leading-relaxed">{item.body}</p>
                </div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Form section */}
      <section className="bg-[#fbfaf8] py-16 md:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.6fr] gap-12 items-start">

            {/* Left — context */}
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:sticky lg:top-28"
            >
              <p className="text-xs font-semibold uppercase tracking-widest text-neutral-400 mb-4">
                Pourquoi cet échange ?
              </p>
              <h2 className="text-3xl font-bold text-neutral-950 leading-tight mb-5">
                Un premier pas simple,{' '}
                <span className={gradientText}>sans pression.</span>
              </h2>
              <p className="text-base text-neutral-600 leading-relaxed mb-6">
                Cet appel est conçu pour comprendre votre situation : vos enjeux
                de recrutement, vos contraintes, vos attentes. Il ne s'agit pas
                d'un rendez-vous commercial, mais d'un vrai échange de fond.
              </p>
              <p className="text-base text-neutral-600 leading-relaxed mb-8">
                Nous parlerons de votre contexte, de la réalité du poste ou du
                projet, et des premières pistes pour avancer.
              </p>

              <div className="rounded-2xl border border-[#d03484]/20 bg-gradient-to-br from-[#d03484]/5 to-[#d47254]/5 p-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-[#d03484] mb-3">
                  Ce que vous obtenez
                </p>
                <ul className="space-y-2.5">
                  {[
                    'Un regard expert sur votre besoin',
                    'Des pistes concrètes, pas du discours',
                    'Un engagement clair sur les étapes suivantes',
                    'Zéro pression, zéro engagement',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-neutral-700">
                      <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center">
                        <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 10 8">
                          <path d="M1 4l2.5 2.5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Right — form */}
            <motion.div
              initial={{ opacity: 0, x: 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <BookingForm />
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
